import { getTotalMatches } from "@/utils/match-count";
import { ordinal, rating } from "openskill";

function getOrdinal(player) {
  return player.tournamentOrdinal;
}

function getStartOfTournamentOrdinal(player) {
  return ordinal(rating({ mu: player.startOfTournamentMu, sigma: player.startOfTournamentSigma }));
}

function selectEligiblePlayers(tournament) {
  let players = tournament.players.filter(p => p.present && !p.active);

  if (tournament.useMaxTotalGames && tournament.maxTotalMatches != null) {
    players = players.filter(p => getTotalMatches(p) < tournament.maxTotalMatches);
  }

  return players;
}

function adjustPositionsByWinLossDifferential(players, winsToAdvanceGroup) {
  const sortedByOrdinal = [...players].sort((a, b) => {
    const ord = getStartOfTournamentOrdinal(a) - getStartOfTournamentOrdinal(b);
    if (ord !== 0) return ord;
    return a.createdAt.toMillis() - b.createdAt.toMillis();
  });

  const withPositions = sortedByOrdinal.map((player, index) => ({
    player,
    position: index
  }));

  const movementNumber = winsToAdvanceGroup > 0 ? 4 / winsToAdvanceGroup : 0;

  return withPositions.map(({ player, position }) => ({
    player,
    newPosition: position + ((player.wins - player.losses) * movementNumber)
  }));
}

function selectPlayersWithFewestGamesUpTo(candidates, totalSpots) {
  const selected = [...candidates]
    .sort((a, b) => {
      const gamesDiff = getTotalMatches(a.player) - getTotalMatches(b.player);
      if (gamesDiff !== 0) return gamesDiff;
      return a.newPosition - b.newPosition;
    })
    .slice(0, totalSpots);

  selected.sort((a, b) => b.newPosition - a.newPosition);

  return selected;
}

function extractSinglesCandidatesFromTail(selected, remainderCount) {
  const singlesPool = selected.filter(p => p.player.userModel.singles);
  if (singlesPool.length < remainderCount) return [];

  const minSinglesCount = Math.min(...singlesPool.map(p => p.player.singlesCount));
  const anchor = selected.find(p => p.player.userModel.singles && p.player.singlesCount === minSinglesCount);
  let targetCount = minSinglesCount;
  const candidates = [anchor];

  while (candidates.length < remainderCount) {
    const remainingPool = selected.filter(p =>
      !candidates.includes(p) &&
      p.player.userModel.singles &&
      p.player.singlesCount === targetCount
    );
    remainingPool.sort((a, b) => {
      const distA = Math.abs(selected.indexOf(a) - selected.indexOf(anchor));
      const distB = Math.abs(selected.indexOf(b) - selected.indexOf(anchor));
      if (distA !== distB) return distA - distB;
      return selected.indexOf(a) - selected.indexOf(b);
    });
    const needed = remainderCount - candidates.length;
    candidates.push(...remainingPool.slice(0, needed));
    if (candidates.length < remainderCount) targetCount++;
  }

  for (const c of candidates) {
    const idx = selected.indexOf(c);
    selected.splice(idx, 1);
  }

  candidates.sort((a, b) => a.newPosition - b.newPosition);
  selected.push(...candidates);

  return candidates;
}

function pickLowestSinglesCountFive(selected) {
  return [...selected]
    .sort((a, b) => {
      const countDiff = a.player.singlesCount - b.player.singlesCount;
      if (countDiff !== 0) return countDiff;
      return a.newPosition - b.newPosition;
    })
    .slice(0, 5);
}

function moveToTail(selected, candidates) {
  for (const c of candidates) {
    const idx = selected.indexOf(c);
    selected.splice(idx, 1);
  }
  candidates.sort((a, b) => a.newPosition - b.newPosition);
  selected.push(...candidates);
}

function buildTeamPairingCounts(tournament) {
  const counts = new Map();
  const inactiveMatches = tournament.inactiveMatches || [];
  for (const match of inactiveMatches) {
    for (const team of match.teams) {
      if (team.playerIds.length === 2) {
        const [a, b] = [...team.playerIds].sort();
        const key = `${a}:${b}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
  }
  return key => counts.get(key) || 0;
}

function evaluateOption(quadruple, indices, getPairingCount) {
  const pair1Key = [quadruple[indices[0]].player.id, quadruple[indices[1]].player.id].sort().join(":");
  const pair2Key = [quadruple[indices[2]].player.id, quadruple[indices[3]].player.id].sort().join(":");
  const count1 = getPairingCount(pair1Key);
  const count2 = getPairingCount(pair2Key);
  return {
    maxCount: Math.max(count1, count2),
    totalCount: count1 + count2,
    indices
  };
}

function formDoublesTeamsFromSelected(selected, getPairingCount, stopIndex = selected.length) {
  const teams = [];
  const matches = [];

  const options = [
    [0, 3, 1, 2],
    [0, 2, 1, 3],
    [0, 1, 2, 3]
  ];

  for (let i = 0; i < stopIndex; i += 4) {
    const quadruple = selected.slice(i, i + 4);
    if (quadruple.length < 4) {
      break;
    }
    quadruple.sort((a, b) => getOrdinal(b.player) - getOrdinal(a.player));

    const scored = options.map(o => evaluateOption(quadruple, o, getPairingCount));
    scored.sort((a, b) => {
      if (a.maxCount !== b.maxCount) return a.maxCount - b.maxCount;
      if (a.totalCount !== b.totalCount) return a.totalCount - b.totalCount;
      return options.indexOf(a.indices) - options.indexOf(b.indices);
    });

    const best = scored[0].indices;
    const team1 = [quadruple[best[0]].player, quadruple[best[1]].player];
    const team2 = [quadruple[best[2]].player, quadruple[best[3]].player];
    const team1Index = teams.length;
    teams.push(team1);
    const team2Index = teams.length;
    teams.push(team2);

    matches.push({ teamIndices: [team1Index, team2Index] });
  }

  return { teams, matches };
}

function formSinglesMatchesForRemainder(selected, teams, matches) {
  const remainder = selected.length % 4;
  if (remainder === 0) return;

  const leftover = selected.slice(selected.length - remainder);
  const singlesEligible = leftover.filter(p => p.player.userModel.singles);

  if (singlesEligible.length === 2 || singlesEligible.length === 3) {
    const teamIndices = singlesEligible.map(p => {
      const idx = teams.length;
      teams.push([p.player]);
      return idx;
    });
    matches.push({ teamIndices });
  }
}

function formSplitMatchesFromTail(selected, teams, matches) {
  const tail = selected.slice(selected.length - 5);
  const ironman = tail.slice(0, 3);
  const singles = tail.slice(3, 5);

  const ironmanIndices = ironman.map(p => {
    const idx = teams.length;
    teams.push([p.player]);
    return idx;
  });
  matches.push({ teamIndices: ironmanIndices });

  const singlesIndices = singles.map(p => {
    const idx = teams.length;
    teams.push([p.player]);
    return idx;
  });
  matches.push({ teamIndices: singlesIndices });
}

export function generateMatchups(tournament) {
  const players = selectEligiblePlayers(tournament);
  const withNewPositions = adjustPositionsByWinLossDifferential(players, tournament.winsToAdvanceGroup);

  const totalSpots = (tournament.numberOfTables - tournament.activeMatches.length) * 4;
  const selected = selectPlayersWithFewestGamesUpTo(withNewPositions, totalSpots);

  const remainderCount = selected.length % 4;
  if (remainderCount > 1 && remainderCount <= 3) {
    extractSinglesCandidatesFromTail(selected, remainderCount);
  }

  let splitReady = false;
  if (remainderCount === 1 && tournament.useMaxTotalGames && selected.length >= 5) {
    const tailFive = pickLowestSinglesCountFive(selected);
    if (tailFive.every(p => p.player.userModel.singles)) {
      moveToTail(selected, tailFive);
      splitReady = true;
    }
  }

  const getPairingCount = buildTeamPairingCounts(tournament);
  const stopIndex = splitReady ? selected.length - 5 : selected.length;
  const { teams, matches } = formDoublesTeamsFromSelected(selected, getPairingCount, stopIndex);
  if (splitReady) {
    formSplitMatchesFromTail(selected, teams, matches);
  } else {
    formSinglesMatchesForRemainder(selected, teams, matches);
  }

  const occupied = new Set(tournament.activeMatches.map(m => m.tableNumber));
  const freeTables = Array.from({ length: tournament.numberOfTables }, (_, i) => i + 1)
    .filter(t => !occupied.has(t));

  matches.forEach((match, i) => {
    match.tableNumber = freeTables[i];
  });

  return { teams, matches };
}
